import { useEffect, useState } from 'react';
import { Copy, Download, Plus, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SlidePanel } from '@/shared/components';
import { useEquipmentModels, useSubmissions } from '../hooks/useEquipment';
import { useEquipmentPermissions } from '../hooks/useEquipmentPermissions';
import { copyEquipmentToClipboard, exportEquipmentCSV, useEquipmentListTable } from '../hooks/useEquipmentListTable';
import { EquipmentListView } from '../components/EquipmentListView';
import { EquipmentDetailPanel } from '../components/EquipmentDetailPanel';
import { EquipmentGuide } from '../components/EquipmentGuide';
import { SubmissionListView } from '../components/review/SubmissionListView';
import { SubmissionPanel } from '../components/review/SubmissionPanel';
import type { EquipmentModelRow } from '../types';

type ActiveTab = 'library' | 'review' | 'archive' | 'guide';

const VALID_TABS: ActiveTab[] = ['library', 'review', 'archive', 'guide'];
const ACTIVE_TAB_KEY = 'atek_equipment_active_tab';
const PANEL_STORAGE_KEY = 'atek_equipment_panel_width';
const REVIEW_PANEL_STORAGE_KEY = 'atek_equipment_review_panel_width';

export default function EquipmentLibraryPage() {
  const { canWrite, canReview } = useEquipmentPermissions();
  const [activeTab, setActiveTab] = useState<ActiveTab>(() => {
    const saved = localStorage.getItem(ACTIVE_TAB_KEY) as ActiveTab | null;
    return saved && VALID_TABS.includes(saved) ? saved : 'library';
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<'view' | 'create'>('view');
  const [panelOpen, setPanelOpen] = useState(false);
  const [submissionId, setSubmissionId] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
  }, [activeTab]);

  const modelsQuery = useEquipmentModels(activeTab === 'archive' ? 'archived' : 'active');
  const pendingQuery = useSubmissions('pending', canReview);
  const pendingCount = pendingQuery.data?.length ?? 0;
  const table = useEquipmentListTable(modelsQuery.data ?? []);
  const isListTab = activeTab === 'library' || activeTab === 'archive';

  const tabs: { value: ActiveTab; label: string }[] = [
    { value: 'library', label: 'Library' },
    ...(canReview ? [{ value: 'review' as const, label: pendingCount ? `Review (${pendingCount})` : 'Review' }] : []),
    { value: 'archive', label: 'Archive' },
    { value: 'guide', label: 'Guide' },
  ];

  const openModel = (model: EquipmentModelRow) => {
    setSelectedId(model.id);
    setPanelMode('view');
    setPanelOpen(true);
  };

  const openCreate = () => {
    setSelectedId(null);
    setPanelMode('create');
    setPanelOpen(true);
  };

  const handlePanelClose = (open: boolean) => {
    setPanelOpen(open);
    if (!open) {
      setSelectedId(null);
      setPanelMode('view');
    }
  };

  const handleCreated = (id: string) => {
    setSelectedId(id);
    setPanelMode('view');
  };

  const handleCopy = async () => {
    await copyEquipmentToClipboard(table.sortedItems, table.visibleColumns, table.ctx);
    toast.success('Copied to clipboard');
  };

  const handleExportCsv = () => {
    exportEquipmentCSV(table.sortedItems, table.visibleColumns, table.ctx);
    toast.success(`Exported ${table.sortedItems.length} rows`);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 border-b border-border flex-wrap">
        <div className="flex items-center gap-0">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setActiveTab(tab.value)}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.value
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isListTab && (
          <div className="flex items-center gap-2 flex-wrap pb-1.5">
            <Button variant="outline" size="sm" onClick={handleCopy}>
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCsv}>
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="h-4 w-4 mr-1" /> Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {table.columns.map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={table.isVisible(col.key)}
                    onCheckedChange={() => table.toggleColumn(col.key)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {canWrite && (
              <Button size="sm" onClick={openCreate}>
                <Plus className="h-4 w-4 mr-1" /> New model
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex min-h-[calc(100vh-240px)]">
        <div className="flex-1 min-w-0 overflow-auto">
          {isListTab && (
            <EquipmentListView
              sortedItems={table.sortedItems}
              visibleColumns={table.visibleColumns}
              columnFilters={table.columnFilters}
              ctx={table.ctx}
              sortKey={table.sortKey}
              sortDirection={table.sortDirection}
              isLoading={modelsQuery.isLoading}
              emptyLabel={activeTab === 'archive' ? 'No archived models.' : 'No equipment models yet.'}
              onSort={table.handleSort}
              onSetColumnFilter={table.setColumnFilter}
              onSelectItem={openModel}
              onCreateItem={canWrite && activeTab === 'library' ? openCreate : undefined}
            />
          )}
          {activeTab === 'review' && canReview && (
            <SubmissionListView
              submissions={pendingQuery.data ?? []}
              isLoading={pendingQuery.isLoading}
              onSelect={(submission) => setSubmissionId(submission.id)}
            />
          )}
          {activeTab === 'guide' && <EquipmentGuide />}
        </div>

        <SlidePanel open={panelOpen && isListTab} storageKey={PANEL_STORAGE_KEY}>
          <EquipmentDetailPanel
            key={panelMode === 'create' ? 'create' : selectedId}
            modelId={selectedId}
            mode={panelMode}
            open={panelOpen}
            onOpenChange={handlePanelClose}
            onCreated={handleCreated}
          />
        </SlidePanel>

        <SlidePanel open={activeTab === 'review' && !!submissionId} storageKey={REVIEW_PANEL_STORAGE_KEY}>
          {submissionId && (
            <SubmissionPanel
              key={submissionId}
              submissionId={submissionId}
              onClose={() => setSubmissionId(null)}
              onReviewed={() => setSubmissionId(null)}
            />
          )}
        </SlidePanel>
      </div>
    </div>
  );
}
